import type { AnyFSA } from '@makemydeal/dr-browser-utils';

// selectors
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { BuildPayloadPurpose } from '@makemydeal/dr-dash-bff-types';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { PROGRAM_QUOTES_REFETCH_META, actions as offerReduxActions } from '@makemydeal/dr-offer-redux';
import { LEASE } from '@makemydeal/dr-platform-types';
import type { ReduxNext, ReduxStore } from '@makemydeal/dr-shared-store';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { PAYMENT_FAILURE, PAYMENT_RECEIVED } from '../actionTypes/deskingActionTypes';
import { OFFER_SAVE_FAILURE, OFFER_SAVE_SUCCESS } from '../actionTypes/offerActionTypes';
import { createLoggingContextSelector } from '../selectors/loggingContextSelector';
import { getCurrentOfferType, getPaymentFrequency } from '../selectors/offerRedux';

const { UPDATED_PAYMENT_FREQUENCY } = offerReduxActions;

// Prefix for all event names
const EVENT_NAME_PREFIX = 'MV:Payment Frequency';

// Store the previous payment frequency in memory to track changes across middleware invocations
let previousPaymentFrequency: string | null | undefined = null;

/**
 * Middleware to track payment frequency changes in New Relic
 *
 * This middleware centralizes all payment frequency tracking logic:
 * - Captures the previous payment frequency when UPDATED_PAYMENT_FREQUENCY action is dispatched
 * - Tracks payment frequency on successful save operations (OFFER_SAVE_SUCCESS) - only when paymentFrequency has changed
 * - Tracks payment frequency on failed save operations (OFFER_SAVE_FAILURE) - only when paymentFrequency has changed
 * - Tracks successful payment frequency changes via Payment Service (PAYMENT_RECEIVED)
 * - Tracks failed payment frequency changes via Payment Service (PAYMENT_FAILURE)
 *
 * Conditions: Only tracks when offerType is LEASE and enableOnePayLease_F231960 feature toggle is enabled
 */
export const middleware = (store: ReduxStore<StateTree>) => (next: ReduxNext) => (action: AnyFSA) => {
    const stateBefore = store.getState();

    // Capture the previous payment frequency BEFORE UPDATED_PAYMENT_FREQUENCY is processed
    if (action.type === UPDATED_PAYMENT_FREQUENCY) {
        previousPaymentFrequency = getPaymentFrequency(stateBefore);
    }

    // Process the action
    next(action);

    // Get the state AFTER the action is processed
    const state = store.getState();

    // Only track payment frequency for LEASE offers when feature toggles are available and the feature toggle is enabled
    const currentOfferType = getCurrentOfferType(state);
    const areFeatureTogglesReady = featureToggleSelectors.areFeatureTogglesAvailable(state);

    if (!areFeatureTogglesReady) {
        return;
    }

    const isOnePayLeaseEnabled = featureToggleSelectors.enableOnePayLease_F231960(state);

    if (currentOfferType !== LEASE || !isOnePayLeaseEnabled) {
        return;
    }

    switch (action.type) {
        case OFFER_SAVE_SUCCESS: {
            const currentPaymentFrequency = getPaymentFrequency(state);

            // Only log if payment frequency has actually changed
            if (previousPaymentFrequency === null || previousPaymentFrequency === currentPaymentFrequency) {
                break;
            }

            const loggingContext = createLoggingContextSelector(state);

            const eventName = `${EVENT_NAME_PREFIX} - Save Offer`;

            const attributes = {
                ...loggingContext,
                paymentFrequency: currentPaymentFrequency,
                previousPaymentFrequency,
                purpose: BuildPayloadPurpose.OfferSave,
                success: true
            };

            logNewRelicPageAction(eventName, attributes);

            // Reset the previous value after logging
            previousPaymentFrequency = null;

            break;
        }

        case OFFER_SAVE_FAILURE: {
            const currentPaymentFrequency = getPaymentFrequency(state);

            // Only log if payment frequency has actually changed
            if (previousPaymentFrequency === null || previousPaymentFrequency === currentPaymentFrequency) {
                break;
            }

            const loggingContext = createLoggingContextSelector(state);
            const error = action.payload;

            const eventName = `${EVENT_NAME_PREFIX} - Save Offer Failed`;

            const attributes = {
                ...loggingContext,
                paymentFrequency: currentPaymentFrequency,
                previousPaymentFrequency,
                purpose: BuildPayloadPurpose.OfferSave,
                success: false,
                error: error?.message || error?.errorMessage || 'Unknown error',
                ...(error && { errorDetails: error })
            };

            logNewRelicPageAction(eventName, attributes);

            // Reset the previous value after logging
            previousPaymentFrequency = null;

            break;
        }

        case PAYMENT_RECEIVED: {
            const originalAction = action.meta?.originalAction;

            // Only track if the original action was a payment frequency update
            if (originalAction?.type === UPDATED_PAYMENT_FREQUENCY) {
                const paymentFrequency = originalAction.payload?.frequency;
                const offerType = originalAction.payload?.offerType;
                const loggingContext = createLoggingContextSelector(state);
                const attributes = {
                    ...loggingContext,
                    paymentFrequency,
                    offerType,
                    success: true
                };

                logNewRelicPageAction(`${EVENT_NAME_PREFIX} - Changed`, attributes);
            }
            break;
        }

        case PAYMENT_FAILURE: {
            const originalAction = action.meta?.originalAction;

            // Only track if the original action was a payment frequency update
            if (originalAction?.type === UPDATED_PAYMENT_FREQUENCY && !originalAction.meta?.[PROGRAM_QUOTES_REFETCH_META]) {
                const paymentFrequency = originalAction.payload?.frequency;
                const offerType = originalAction.payload?.offerType;
                const loggingContext = createLoggingContextSelector(state);
                const error = action.meta.error;

                const attributes = {
                    ...loggingContext,
                    paymentFrequency,
                    offerType,
                    success: false,
                    error: error?.message || 'Unknown error',
                    ...(error && { errorDetails: error })
                };

                logNewRelicPageAction(`${EVENT_NAME_PREFIX} - Change Failed`, attributes);
                store.dispatch({
                    type: UPDATED_PAYMENT_FREQUENCY,
                    payload: {
                        frequency: previousPaymentFrequency,
                        offerType: currentOfferType
                    }
                });
            }
            break;
        }

        default:
            break;
    }
};
